// ULTester.tsx
import { Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, CardContent, Grid, Stack, TextField, Typography } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DraggableRow from './DraggableRow';
import DropSpacer from './DropSpacer';
import { useStore } from 'hooks/useStore';
import LessonReportTemplateAddStore from './LessonReportTemplateAddStore';
import PageTitle from 'presenters/Dashboard/PageTitle';
import PageContentArea from 'presenters/Dashboard/PageContentArea';
import SaveAlt from '@mui/icons-material/SaveAlt';
import AddIcon from '@mui/icons-material/Add';
import { ServiceConfig } from 'constants/shared/ServiceConfig';
import { useForm } from 'react-hook-form';

import {
  lessonReportTemplateAddValidationSchema,
  initialLessonReportTemplateAddFormValues,
  LessonReportTemplateFormValuesT,
} from './formData';
import DisappearingRow from './DisappearingRow';
import { yupResolver } from '@hookform/resolvers/yup';

export const ANIMATION_DURATION_MS = 300;

const LessonReportTemplateAddContainer = observer(() => {
  const { addRow, templateItems, handleDrop, disappearingRowAfterIndex, handleSubmit } =
    useStore<LessonReportTemplateAddStore>('LessonReportTemplateAddStore');

  const {
    register,
    handleSubmit: rhfSubmit,
    formState: { errors, touchedFields, isSubmitting },
    reset, // initial 값을 동적으로 바꾸면 여기서 reset으로 재초기화
  } = useForm<LessonReportTemplateFormValuesT>({
    defaultValues: initialLessonReportTemplateAddFormValues,
    resolver: yupResolver(lessonReportTemplateAddValidationSchema),
    mode: 'onBlur',
  });

  return (
    <PageContentArea>
      <PageTitle
        title="새 수업 리포트 템플릿"
        description="수업리포트를 작성할 때 사용되는 기본 형태를 정의합니다."
      >
        <Button
          startIcon={<AddIcon fontSize="small" />}
          variant="contained"
          onClick={addRow}
          disabled={templateItems.length >= ServiceConfig.LessonReport.maxItemCount}
        >
          항목 추가
        </Button>
        <Button
          startIcon={<SaveAlt fontSize="small" />}
          variant="contained"
          type="submit"
          form="lesson-report-template-add-form"
          disabled={isSubmitting}
        >
          리포트 템플릿 저장
        </Button>
      </PageTitle>
      <DndProvider backend={HTML5Backend}>
        <form id="lesson-report-template-add-form" noValidate onSubmit={rhfSubmit(handleSubmit)}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Typography variant="h6">기본 정보</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="템플릿 제목"
                      placeholder="예: 고등부 데일리 리포트"
                      type="text"
                      required
                      inputProps={{
                        maxLength: ServiceConfig.LessonReport.maxTemplateTitleLength,
                      }}
                      {...register('templateTitle')}
                      error={!!(touchedFields.templateTitle && errors.templateTitle)}
                      helperText={touchedFields.templateTitle && errors.templateTitle?.message}
                    />
                    <TextField
                      fullWidth
                      label="학부모에게 보여지는 레포트 제목"
                      placeholder="예: 고등부 데일리 리포트"
                      type="text"
                      required
                      inputProps={{
                        maxLength: ServiceConfig.LessonReport.maxTemplateTitleLength,
                      }}
                      {...register('publicTitle')}
                      error={!!(touchedFields.publicTitle && errors.publicTitle)}
                      helperText={touchedFields.publicTitle && errors.publicTitle?.message}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
        <Stack spacing={0}>
          <DropSpacer index={0} onDropAt={handleDrop} />
          {templateItems.map((item, i) => {
            // 나타나는 쪽(tick)까지 같이 쓰고 있다면 key는 이렇게
            const key = item.no; // === lastMovedNo ? `${item.no}-${moveTick}` : String(item.no);

            return (
              <Fragment key={key}>
                <DraggableRow index={i} />

                {/* 바로 이 index 뒤에 disappearing dummy 삽입 */}
                {i === disappearingRowAfterIndex ? <DisappearingRow /> : null}

                <DropSpacer index={i + 1} onDropAt={handleDrop} />
              </Fragment>
            );
          })}
        </Stack>
      </DndProvider>
    </PageContentArea>
  );
});

export default LessonReportTemplateAddContainer;
